import { LOOT, WALL, shopItemById } from "./config.ts";
import { computeLevels } from "./levels.ts";
import { clampInt } from "./time.ts";
import type { ShopItem, ShopResult, VillageMetrics, VillageState } from "./types.ts";

function withWallLevel(state: VillageState, metrics: VillageMetrics): VillageState {
  const { levels } = computeLevels(metrics, state.wallHp);
  return { ...state, levels: { ...state.levels, walls: levels.walls } };
}

export function buyItem(
  state: VillageState,
  itemId: string,
  metrics: VillageMetrics,
): ShopResult {
  const item = shopItemById(itemId);
  if (!item) {
    return { ok: false, error: "unknown_item" };
  }
  if (state.gold < item.goldCost || state.crystals < item.crystalCost) {
    return { ok: false, error: "cannot_afford" };
  }

  if (item.kind === "repair") {
    if (state.wallHp >= WALL.maxHp) {
      return { ok: false, error: "walls_full" };
    }
    const next: VillageState = {
      ...state,
      gold: clampInt(state.gold - item.goldCost, 0, LOOT.maxCarry),
      crystals: clampInt(state.crystals - item.crystalCost, 0, LOOT.maxCarry),
      wallHp: item.restoreFull
        ? WALL.maxHp
        : clampInt(state.wallHp + (item.wallHp ?? 0), 0, WALL.maxHp),
      justLeveled: [],
      raid: state.raid,
      collect: state.collect,
    };
    return { ok: true, state: withWallLevel(next, metrics), item };
  }

  if (item.kind === "decoration") {
    const decorationId = item.decorationId;
    if (!decorationId) {
      return { ok: false, error: "unknown_item" };
    }
    if (state.decorations.includes(decorationId)) {
      return { ok: false, error: "already_owned" };
    }
    const next: VillageState = {
      ...state,
      gold: clampInt(state.gold - item.goldCost, 0, LOOT.maxCarry),
      crystals: clampInt(state.crystals - item.crystalCost, 0, LOOT.maxCarry),
      decorations: [...state.decorations, decorationId],
      justLeveled: [],
    };
    return { ok: true, state: next, item };
  }

  const neverKind: never = item.kind;
  throw new Error(`Unhandled shop kind: ${neverKind}`);
}

export function canBuy(state: VillageState, item: ShopItem): boolean {
  if (state.gold < item.goldCost || state.crystals < item.crystalCost) {
    return false;
  }
  if (item.kind === "repair") {
    return state.wallHp < WALL.maxHp;
  }
  if (item.kind === "decoration" && item.decorationId) {
    return !state.decorations.includes(item.decorationId);
  }
  return false;
}
